import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import * as apiActions from '../actions/apiActions';

/**
 * Loads the next page of entries when the reader nears the end of the feed.
 *
 * Replaces the First/Prev/Next/Last pagination bar. The "Load more" button is
 * the real control and always works; the IntersectionObserver just presses it
 * early so scrolling feels continuous. Keeping the button means keyboard and
 * screen-reader users still have a way down, and browsers without
 * IntersectionObserver degrade to plain click-to-load rather than a dead end.
 */
class InfiniteScrollContainer extends Component {
	constructor( props ) {
		super( props );
		this.sentinel = React.createRef();
		this.observer = null;
		this.loadMore = this.loadMore.bind( this );
	}

	componentDidMount() {
		if ( ! window.IntersectionObserver || ! this.sentinel.current ) {
			return;
		}

		this.observer = new window.IntersectionObserver(
			( entries ) => {
				if ( entries.some( ( entry ) => entry.isIntersecting ) ) {
					this.loadMore();
				}
			},
			// Start fetching before the sentinel is actually visible so the next
			// entries are usually in place by the time the reader gets to them.
			{ rootMargin: '400px 0px' }
		);

		this.observer.observe( this.sentinel.current );
	}

	componentWillUnmount() {
		if ( this.observer ) {
			this.observer.disconnect();
		}
	}

	loadMore() {
		const { page, pages, loadingMore, getEntriesAppend } = this.props;
		if ( loadingMore || page >= pages ) {
			return;
		}
		getEntriesAppend( page + 1 );
	}

	render() {
		const { page, pages, loadingMore } = this.props;
		const hasMore = page < pages;

		return (
			<div className="liveblog-infinite-scroll">
				{ /* Observed even on the last page: entries appended above it move the
            sentinel, and re-observing on every state change would be churn. */ }
				<div
					ref={ this.sentinel }
					className="liveblog-infinite-scroll-sentinel"
					aria-hidden="true"
				/>
				<div
					className="liveblog-infinite-scroll-status"
					aria-live="polite"
				>
					{ loadingMore && (
						<span className="liveblog-infinite-scroll-loading">
							Loading more updates…
						</span>
					) }
					{ ! loadingMore && hasMore && (
						<button
							className="liveblog-btn liveblog-infinite-scroll-btn"
							onClick={ this.loadMore }
						>
							Load more updates
						</button>
					) }
					{ ! hasMore && pages > 1 && (
						<span className="liveblog-infinite-scroll-end">
							You&rsquo;re all caught up.
						</span>
					) }
				</div>
			</div>
		);
	}
}

InfiniteScrollContainer.propTypes = {
	page: PropTypes.number,
	pages: PropTypes.number,
	loadingMore: PropTypes.bool,
	getEntriesAppend: PropTypes.func,
};

const mapStateToProps = ( state ) => ( {
	page: state.pagination.page,
	pages: state.pagination.pages,
	loadingMore: state.api.loadingMore,
} );

const mapDispatchToProps = ( dispatch ) =>
	bindActionCreators(
		{
			...apiActions,
		},
		dispatch
	);

export default connect(
	mapStateToProps,
	mapDispatchToProps
)( InfiniteScrollContainer );
